import { jsx as _jsx } from "react/jsx-runtime";
import { VirtualizeMountAllContext } from '#pplx/frontend/common/VirtualizeMountAllContext.js';
import React, { useContext, useEffect, useLayoutEffect, useRef, useState, useTransition, } from 'react';
export const Virtualize = React.memo(function Virtualize({ children, pinned = false, estimate = 80, measureKey, overscan = '100% 0px', onVisibleChange, root, }) {
    // The placeholder height only matters while the content is hidden. Track the
    // live height in a ref and commit it on every visible-to-hidden transition:
    // a state update per ResizeObserver callback added a React commit for every
    // streamed token.
    const [height, setHeight] = useState(0);
    const measuredHeight = useRef(0);
    const [visible, setVisible] = useState(pinned);
    const [, tx] = useTransition();
    const outer = useRef(null);
    const inner = useRef(null);
    const lastMeasureKey = useRef(measureKey);
    const mountAll = useContext(VirtualizeMountAllContext);
    const show = pinned || visible || mountAll;
    useLayoutEffect(() => {
        const measureKeyChanged = lastMeasureKey.current !== measureKey;
        lastMeasureKey.current = measureKey;
        const el = inner.current;
        if (!el || !show) {
            if (measureKeyChanged) {
                measuredHeight.current = 0;
                setHeight(0);
            }
            else if (measuredHeight.current > 0) {
                // Unpinning hides content without an intersection event, so the commit
                // has to happen here rather than in the observer callback.
                setHeight(measuredHeight.current);
            }
            return;
        }
        const next = el.getBoundingClientRect().height;
        if (next > 0)
            measuredHeight.current = next;
    }, [show, measureKey]);
    useEffect(() => {
        const el = inner.current;
        if (!el)
            return;
        const ro = new ResizeObserver(([e]) => {
            if (!e)
                return;
            measuredHeight.current = e.contentRect.height || measuredHeight.current;
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    useEffect(() => {
        const el = outer.current;
        if (!el)
            return;
        let isIntersecting = false;
        const io = new IntersectionObserver(([e]) => {
            if (!e)
                return;
            isIntersecting = e.isIntersecting;
            // Mount at normal priority: a transition is starved by the sync store
            // updates of a streaming answer, which leaves the placeholder blank while
            // the user scrolls. Unmounting can stay deferred.
            if (e.isIntersecting) {
                setVisible(true);
                onVisibleChange?.(true, el);
                return;
            }
            tx(() => {
                setVisible(false);
                onVisibleChange?.(false, el);
            });
        }, { rootMargin: overscan, root: root?.current });
        io.observe(el);
        return () => {
            io.disconnect();
            if (isIntersecting)
                onVisibleChange?.(false, el);
        };
    }, [overscan, onVisibleChange, root]);
    return (_jsx("div", { ref: outer, style: !pinned && !show ? { minHeight: height || estimate } : undefined, className: "w-full", children: _jsx("div", { ref: inner, className: "w-full flex flex-col", children: show ? children : null }) }));
});
//# sourceMappingURL=Virtualize.js.map